<script setup lang="ts">
import { onMounted, computed, ref, watch } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ContactTable from '@/components/features/contact/ContactTable.vue'
import type { FilterModel } from 'ag-grid-community'
import { useContactStore } from '@/stores/useContactStore'

import ContactCreateModal from '@/components/features/contact/ContactCreateModal.vue'

const contactStore = useContactStore()

const filters = ref<Record<string, any>>({})
const sort = ref<{ column: string; direction: string } | null>(null)
const limit = ref<number>(20)
const page = ref<number>(1)

onMounted(async () => {
  await fetchContacts()
})

let timeout: any = null
watch(
  filters,
  () => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => {
      page.value = 1
      fetchContacts()
    }, 500)
  },
  { deep: true },
)

watch(
  sort,
  () => {
    fetchContacts()
  }
)

async function fetchContacts() {
  const payload: any = {}
  if (filters.value.firstname?.filter) payload.firstname = filters.value.firstname.filter
  if (filters.value.lastname?.filter) payload.lastname = filters.value.lastname.filter
  if (filters.value.email?.filter) payload.email = filters.value.email.filter
  if (filters.value.isSigner !== undefined) payload.isSigner = filters.value.isSigner
  if (filters.value.search) payload.search = filters.value.search
  if (filters.value.companyUuid && filters.value.companyUuid.length > 0) payload.companyUuid = filters.value.companyUuid

  if (sort.value) payload.sort = sort.value

  await contactStore.fetchContacts(payload, page.value, limit.value)
}

async function updateFilter(filtersModel: FilterModel) {
  filters.value = { ...filters.value, ...filtersModel }
}

async function updateSort(sortModel: any) {
  filters.value.sort = sortModel ? { column: sortModel.colId, direction: sortModel.sort === 'asc' ? 'asc' : 'desc' } : null
}

async function updateSearch(search: string) {
  if (search) {
    filters.value.search = search
  } else {
    if (Object.keys(filters.value).includes('search')) {
      delete filters.value.search
    }
  }
}

async function updateCompanies(companyUuids: string[]) {
  if (companyUuids && companyUuids.length > 0) {
    filters.value.companyUuid = companyUuids
  } else {
    if (Object.keys(filters.value).includes('companyUuid')) delete filters.value.companyUuid
  }
}

async function updateSigner(isSigner: boolean | undefined) {
  if (isSigner !== undefined) {
    filters.value.isSigner = isSigner
  } else {
    if (Object.keys(filters.value).includes('isSigner')) delete filters.value.isSigner
  }
}

function clearFilters() {
  filters.value = {}
  page.value = 1
}

async function onPaginationChanged(newLimit: number, newPage: number) {
  if (limit.value === newLimit && page.value === newPage) return
  limit.value = newLimit
  page.value = newPage
  await fetchContacts()
}

const gridFilterModel = computed(() => {
  return Object.keys(filters.value).length > 0 ? filters.value : undefined
})
</script>

<template>
  <div class="p-6">
    <div class="flex items-center justify-between gap-4">
      <Bs2Title title="Gestion des contacts" subtitle="Consultez et gérez la liste de vos contacts" />
    </div>

    <div class="mt-8 card bg-base-100 shadow">
      <div class="card-body">
        <ContactTable
          :allow-sorting="true"
          :allow-filtering="true"
          :allow-export="false"
          :show-companies-filter="true"
          :rows="contactStore.contacts?.data || []"
          :filter-model="gridFilterModel"
          :use-server-side-pagination="true"
          :pagination-page-size="contactStore.contacts?.limit || 20"
          :pagination-total-rows="contactStore.contacts?.total || 0"
          :current-page="contactStore.contacts?.page || 1"
          :total-pages="contactStore.contacts?.totalPages || 1"
          :isLoading="contactStore.isLoadingContacts"
          @updateFilter="updateFilter"
          @updateSort="updateSort"
          @updateSearch="updateSearch"
          @updateCompanies="updateCompanies"
          @updateSigner="updateSigner"
          @clearFilters="clearFilters"
          @paginationChanged="onPaginationChanged"
          @deleted="fetchContacts"
        >
          <template #button>
            <ContactCreateModal @created="fetchContacts" />
          </template>
        </ContactTable>
      </div>
    </div>
  </div>
</template>
